import "./verification.css";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../../hooks/AuthProvider";
import API_BASE_URL from "../../../config/api";
import { Modal, Button } from "react-bootstrap";
import {
  Briefcase,
  Tag,
  Globe,
  Envelope,
  GeoAlt,
  ClockHistory,
} from "react-bootstrap-icons";

const statusOptions = ["all", "pending", "verified", "rejected"];
const statusColors = {
  pending: "verifPending",
  verified: "verifVerified",
  rejected: "verifRejected",
  none: "verifNone",
};
const typeLabels = { none: "None", business: "Business", official: "Official" };

function formatDate(ts) {
  if (!ts) return "-";
  const t = Number(ts);
  if (!t) return "-";
  const d = t < 1e12 ? t * 1000 : t;
  return new Date(d).toLocaleString();
}

function businessFields(u) {
  return [
    { icon: <Briefcase />, label: "Business Name", value: u.businessName },
    { icon: <Tag />, label: "Category", value: u.businessCategory },
    { icon: <Globe />, label: "Website", value: u.website },
    { icon: <Envelope />, label: "Business Email", value: u.businessEmail },
    { icon: <GeoAlt />, label: "Address", value: u.businessAddress },
    { icon: <ClockHistory />, label: "Working Hours", value: u.workingHours },
  ];
}

function hasBusinessData(u) {
  return businessFields(u).some((f) => f.value);
}

function Verification() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { token } = useAuth();

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/v1/verification`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      const filtered = items.filter((u) => u.verificationStatus !== "none");
      setUsers(filtered);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to load users");
      console.error(err.message);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const visible = statusFilter === "all"
    ? users
    : users.filter((u) => u.verificationStatus === statusFilter);

  const handleDecision = async () => {
    if (!confirmTarget) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE_URL}/api/v1/verification`, {
        targetUid: confirmTarget.uid,
        isApproved: confirmTarget.approve,
      }, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      setSubmitting(false);
      setConfirmTarget(null);
      fetchUsers();
    } catch (err) {
      setSubmitting(false);
      setError(err.response?.data?.message || err.message || "Action failed");
      console.error(err.message);
    }
  };

  const askDecision = (u, approve) => {
    setReviewTarget(null);
    setConfirmTarget({ uid: u.uid, username: u.username, businessName: u.businessName, approve });
  };

  const businessName = (u) => u.businessName || u.businessCategory || "-";

  return (
    <div>
      <div className="verifHeader">
        <div>
          <h3>Business Verification</h3>
          <p>Review the business metadata of each applicant before approving. A system message is sent to the user on every decision.</p>
        </div>
        <button className="verifRefreshBtn" onClick={fetchUsers}>
          Refresh
        </button>
      </div>

      <div className="verifTabs">
        {statusOptions.map((s) => (
          <button
            key={s}
            className={statusFilter === s ? "active" : ""}
            onClick={() => setStatusFilter(s)}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {loading && <div className="verifEmpty"><p>Loading users...</p></div>}

      {error && (
        <div className="verifError">
          <strong>Error:</strong> {error}
          <br />
          <button onClick={fetchUsers}>Retry</button>
        </div>
      )}

      {!loading && !error && visible.length === 0 && (
        <div className="verifEmpty">
          <h4>No users here</h4>
          <p>Users who apply for business verification will appear here.</p>
        </div>
      )}

      {!loading && visible.length > 0 && (
        <div className="verifTable">
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>Business</th>
                <th>User ID</th>
                <th>Verified Type</th>
                <th>Status</th>
                <th>Requested</th>
                <th style={{ width: 290 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((u) => (
                <tr key={u.uid}>
                  <td>
                    <div className="verifUserCell">
                      <div className="verifAvatar">
                        {u.photo ? (
                          <img src={u.photo} alt="" />
                        ) : (
                          (u.username || "?")[0].toUpperCase()
                        )}
                      </div>
                      <div className="verifUserInfo">
                        <strong>{u.username}</strong>
                        <small>{u.email || u.phone || ""}</small>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="verifUserInfo">
                      <strong>{businessName(u)}</strong>
                      {u.businessCategory && <small>{u.businessCategory}</small>}
                    </div>
                  </td>
                  <td className="verifIdCell">{u.uid}</td>
                  <td>{typeLabels[u.verifiedType] || u.verifiedType}</td>
                  <td>
                    <span className={`verifBadge ${statusColors[u.verificationStatus] || "verifNone"}`}>
                      {u.verificationStatus}
                    </span>
                  </td>
                  <td className="verifDateCell">{formatDate(u.requestedAt)}</td>
                  <td>
                    <button
                      className="verifReviewBtn"
                      onClick={() => setReviewTarget(u)}
                    >
                      Review
                    </button>
                    <button
                      className="verifApproveBtn"
                      onClick={() => askDecision(u, true)}
                    >
                      Approve
                    </button>
                    <button
                      className="verifRejectBtn"
                      onClick={() => askDecision(u, false)}
                    >
                      Reject
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal show={!!reviewTarget} onHide={() => setReviewTarget(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Review Business Application</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {reviewTarget && (
            <>
              <div className="verifReviewUser">
                <div className="verifAvatar verifAvatarLg">
                  {reviewTarget.photo ? (
                    <img src={reviewTarget.photo} alt="" />
                  ) : (
                    (reviewTarget.username || "?")[0].toUpperCase()
                  )}
                </div>
                <div className="verifUserInfo">
                  <h4>{reviewTarget.username}</h4>
                  <small>{reviewTarget.email || reviewTarget.phone || reviewTarget.uid}</small>
                  <span className={`verifBadge ${statusColors[reviewTarget.verificationStatus] || "verifNone"}`}>
                    {reviewTarget.verificationStatus}
                  </span>
                </div>
              </div>

              {hasBusinessData(reviewTarget) ? (
                <div className="verifReviewGrid">
                  {businessFields(reviewTarget).map((f, i) =>
                    f.value ? (
                      <div key={i} className="verifReviewField">
                        <div className="verifReviewFieldIcon">{f.icon}</div>
                        <div className="verifUserInfo">
                          <small>{f.label}</small>
                          <strong>{f.value}</strong>
                        </div>
                      </div>
                    ) : null
                  )}
                </div>
              ) : (
                <p className="verifNoBusiness">
                  This applicant has not provided any business metadata yet.
                </p>
              )}

              <div className="verifReviewActions">
                <Button variant="success" onClick={() => askDecision(reviewTarget, true)}>
                  Approve
                </Button>
                <Button variant="danger" onClick={() => askDecision(reviewTarget, false)}>
                  Reject
                </Button>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={!!confirmTarget} onHide={() => setConfirmTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{confirmTarget?.approve ? "Approve Business" : "Reject Business"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirmTarget && (
            <>
              Are you sure you want to{" "}
              <strong>{confirmTarget.approve ? "approve" : "reject"}</strong> business verification for{" "}
              <strong>{confirmTarget.username}</strong>
              {confirmTarget.businessName && confirmTarget.businessName !== "-" ? ` (${confirmTarget.businessName})` : ""}?
              <br />
              <br />
              {confirmTarget.approve
                ? "They will receive the Gold Badge status and a confirmation message from SOLOCHAT_OFFICIAL."
                : "Their request will be marked as rejected and a notification message will be sent from SOLOCHAT_OFFICIAL."}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmTarget(null)}>Cancel</Button>
          <Button
            variant={confirmTarget?.approve ? "success" : "danger"}
            disabled={submitting}
            onClick={handleDecision}
          >
            {submitting ? "Saving..." : (confirmTarget?.approve ? "Approve" : "Reject")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default Verification;